import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { newId } from '@/lib/id';
import { prepareForUpload } from '@/features/messages/attachments';

/**
 * מסמכים ופרטים רגישים של עובד (מיגרציה 092).
 *
 * שום דבר כאן אינו נקרא מטבלה. employee_private סגורה לחלוטין ברמת
 * ה API, והגישה היחידה היא דרך שתי פונקציות שגם רושמות ביומן מי
 * הסתכל ומתי. זה אומר שאין "לטעון מראש" ואין להחזיק במטמון: כל
 * פתיחה של הפאנל היא שורה ביומן, וזו המטרה.
 */

export type PrivateDetails = {
  employee_id: string;
  national_id: string | null;
  id_doc_path: string | null;
  bank_number: string | null;
  bank_branch_number: string | null;
  bank_account_number: string | null;
  updated_at: string | null;
};

export type PrivateStatus = {
  has_id_doc: boolean;
  has_national_id: boolean;
  has_bank: boolean;
  updated_at: string | null;
} | null;

/** נטען רק כשהפאנל פתוח, כי כל טעינה נרשמת כצפייה. */
export function usePrivateDetails(employeeId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['employee-private', employeeId],
    enabled: Boolean(employeeId) && enabled,
    gcTime: 0,
    staleTime: 0,
    queryFn: async (): Promise<PrivateDetails> => {
      const { data, error } = await supabase.rpc('employee_private_get', {
        p_employee: employeeId as string,
      });
      if (error) throw error;
      return data as unknown as PrivateDetails;
    },
  });
}

/** מה קיים, בלי להציג. אינו נחשב צפייה. */
export function usePrivateStatus(employeeId: string | null) {
  return useQuery({
    queryKey: ['employee-private-status', employeeId],
    enabled: Boolean(employeeId),
    queryFn: async (): Promise<PrivateStatus> => {
      const { data, error } = await supabase.rpc('employee_private_status', {
        p_employee: employeeId as string,
      });
      if (error) throw error;
      return (data ?? null) as PrivateStatus;
    },
  });
}

export function usePrivateMutations(employeeId: string | null) {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['employee-private', employeeId] });
    void queryClient.invalidateQueries({ queryKey: ['employee-private-status', employeeId] });
    // התצוגה המקדימה של החוזה נשענת על אותם פרטים. בלי זה
    // משלימים ת"ז ממש מעל, והתצוגה ממשיכה לומר שהיא חסרה.
    void queryClient.invalidateQueries({ queryKey: ['rendered-contract'] });
  };

  const save = useMutation({
    mutationFn: async (input: {
      national_id: string;
      id_doc_path: string | null;
      bank_number: string;
      bank_branch_number: string;
      bank_account_number: string;
    }) => {
      const { error } = await supabase.rpc('save_employee_private', {
        p_employee: employeeId as string,
        p_national_id: input.national_id,
        p_id_doc_path: input.id_doc_path ?? '',
        p_bank_number: input.bank_number,
        p_bank_branch_number: input.bank_branch_number,
        p_bank_account_number: input.bank_account_number,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { save };
}

/**
 * העלאה לבדל hr-docs. הנתיב מתחיל במזהה העובד, וזו הדרך שבה
 * המדיניות על האחסון יודעת של מי הקובץ.
 */
export async function uploadHrDoc(
  employeeId: string,
  kind: 'id' | 'contract' | 'signature',
  file: File | Blob,
  contentType?: string,
): Promise<string> {
  const type = contentType ?? (file instanceof File ? file.type : 'image/png');
  const prepared = file instanceof File ? await prepareForUpload(file) : file;
  const ext = type === 'application/pdf' ? 'pdf' : (type.split('/')[1]?.replace('jpeg', 'jpg') ?? 'jpg');
  const path = `${employeeId}/${kind}-${newId()}.${ext}`;

  const { error } = await supabase.storage.from('hr-docs').upload(path, prepared, {
    contentType: type || 'application/octet-stream',
    upsert: false,
  });
  if (error) throw error;
  return path;
}

export function useHrDocUrl(path: string | null | undefined) {
  return useQuery({
    queryKey: ['hr-doc', path],
    enabled: Boolean(path),
    staleTime: 50 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.storage
        .from('hr-docs')
        .createSignedUrl(path as string, 3600);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}


// ---------- חוזים ----------

export type Contract = {
  id: string;
  employee_id: string;
  title: string;
  body: string | null;
  file_path: string | null;
  status: 'pending' | 'signed' | 'cancelled';
  signature_path: string | null;
  signed_name: string | null;
  signed_at: string | null;
  created_at: string;
};

export function useContracts(employeeId: string | null) {
  return useQuery({
    queryKey: ['employee-contracts', employeeId],
    enabled: Boolean(employeeId),
    queryFn: async (): Promise<Contract[]> => {
      const { data, error } = await supabase
        .from('employee_contracts')
        .select(
          'id, employee_id, title, body, file_path, status, signature_path, signed_name, signed_at, created_at',
        )
        .eq('employee_id', employeeId as string)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Contract[];
    },
  });
}

/** החוזים שלי, לאפליקציה של העובד. */
export function useMyContracts(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['my-contracts', employeeId],
    enabled: Boolean(employeeId),
    queryFn: async (): Promise<Contract[]> => {
      const { data, error } = await supabase
        .from('employee_contracts')
        .select(
          'id, employee_id, title, body, file_path, status, signature_path, signed_name, signed_at, created_at',
        )
        .eq('employee_id', employeeId as string)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Contract[];
    },
  });
}

export function useContractMutations(employeeId: string | null) {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['employee-contracts', employeeId] });
    void queryClient.invalidateQueries({ queryKey: ['my-contracts'] });
  };

  const create = useMutation({
    mutationFn: async (input: {
      title: string;
      body: string | null;
      file_path: string | null;
      created_by: string | null;
    }) => {
      const { error } = await supabase.from('employee_contracts').insert({
        employee_id: employeeId as string,
        title: input.title,
        body: input.body,
        file_path: input.file_path,
        created_by: input.created_by,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const cancel = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('employee_contracts')
        .update({ status: 'cancelled' })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { create, cancel };
}

/** החתימה עצמה. השעה נקבעת בשרת, ולא בדפדפן של החותם. */
export function useSignContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { contractId: string; signaturePath: string; name: string }) => {
      const { error } = await supabase.rpc('sign_contract', {
        p_contract: input.contractId,
        p_signature_path: input.signaturePath,
        p_name: input.name,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-contracts'] });
      void queryClient.invalidateQueries({ queryKey: ['employee-contracts'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export const contractStatusLabel: Record<Contract['status'], string> = {
  pending: 'ממתין לחתימה',
  signed: 'נחתם',
  cancelled: 'בוטל',
};

export const contractStatusTone: Record<Contract['status'], 'good' | 'warn' | 'bad'> = {
  pending: 'warn',
  signed: 'good',
  cancelled: 'bad',
};
